// Share a page as an image (IDEATION → MVP 11). Device role.
//
// Draws the page on the device's own LCD look — shell, bezel, glass, the same
// fonts and the same pixel emoji as the screen — onto a 1080×1080 canvas with
// the footer `PAGE ME · <origin>`, then hands it to the share sheet on native
// or downloads it on the web (core/export.js → core/native.js shareFile).
//
// Every colour is read from the CSS custom properties in app.css at draw time,
// so the image matches the app, including the high-contrast setting.

import { cssColor, drawEmoji } from './emoji-raster.js';
import { graphemes, isEmoji } from './text.js';
import { formatTime, formatStamp, formatNumber } from './format.js';
import { hintFor } from './codebook.js';
import { deviceById } from './devices.js';
import { friendName } from '../store.js';
import { canvasToBlob, shareOrDownload } from '../core/export.js';
import { shareOrigin } from '../core/native.js';

export const SHARE_SIZE = 1080;

const SEG_ONLY = /^[0-9 -]+$/;

function glassColours(device) {
  const hc = typeof document !== 'undefined' && document.documentElement.dataset.lcdContrast === 'high';
  if (hc) return { glass: cssColor('--lcd-hc-glass'), ink: cssColor('--lcd-hc-ink'), ghost: '', tint: '' };
  if (device.lcd === 'blue') {
    return { glass: cssColor('--lcd-blue-glass'), ink: cssColor('--lcd-blue-ink'), ghost: '', tint: '--lcd-blue-glass' };
  }
  if (device.lcd === 'color') {
    return { glass: cssColor('--lcd-color-bg'), ink: cssColor('--lcd-color-text'), ghost: '', tint: '', accent: cssColor('--lcd-color-accent') };
  }
  return { glass: cssColor('--lcd-glass'), ink: cssColor('--lcd-ink'), ghost: cssColor('--lcd-ghost'), tint: '--lcd-glass' };
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(x, y, w, h, r);
  else ctx.rect(x, y, w, h);
}

async function fontsReady() {
  try {
    if (!document.fonts?.load) return;
    await Promise.all([
      document.fonts.load('64px "VT323"'),
      document.fonts.load('64px "DSEG7 Classic"'),
    ]);
  } catch { /* the fallback face is fine */ }
}

/**
 * Lay the body out as lines of tokens that fit `maxW` at `size`. Emoji are one
 * cell wide (the font size); words wrap whole unless a single word is wider
 * than the line, which is then broken by grapheme.
 */
function layout(ctx, body, size, maxW) {
  const cells = graphemes(body).map((g) => ({
    g,
    emoji: isEmoji(g),
    w: isEmoji(g) ? size * 1.05 : ctx.measureText(g).width,
  }));
  const words = [];
  let word = [];
  for (const c of cells) {
    if (c.g === ' ') { if (word.length) words.push(word); words.push([c]); word = []; } else word.push(c);
  }
  if (word.length) words.push(word);

  const lines = [];
  let line = [];
  let lineW = 0;
  const push = () => { lines.push(line); line = []; lineW = 0; };
  for (const w of words) {
    const ww = w.reduce((s, c) => s + c.w, 0);
    const isSpace = w.length === 1 && w[0].g === ' ';
    if (isSpace && !line.length) continue;
    if (lineW + ww <= maxW) { line.push(...w); lineW += ww; continue; }
    if (isSpace) { push(); continue; }
    if (ww <= maxW) { push(); line.push(...w); lineW = ww; continue; }
    for (const c of w) {
      if (lineW + c.w > maxW && line.length) push();
      line.push(c); lineW += c.w;
    }
  }
  if (line.length) lines.push(line);
  return lines;
}

function drawCells(ctx, cells, x, y, size, tintVar) {
  let cx = x;
  for (const c of cells) {
    if (c.emoji) {
      const img = drawEmoji(c.g, { tint: cssColor(tintVar) });
      if (img) {
        const prev = ctx.imageSmoothingEnabled;
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, cx, y - size * 0.82, size, size);
        ctx.imageSmoothingEnabled = prev;
      } else {
        ctx.fillText(c.g, cx, y);
      }
    } else {
      ctx.fillText(c.g, cx, y);
    }
    cx += c.w;
  }
}

/** Render the page to a canvas. Returns the canvas (1080×1080). */
export async function renderPageImage(page, deviceOrId) {
  const device = typeof deviceOrId === 'string' || !deviceOrId ? deviceById(deviceOrId) : deviceOrId;
  await fontsReady();

  const S = SHARE_SIZE;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');
  const colours = glassColours(device);
  const shell = cssColor(device.shell);
  const edge = cssColor(`${device.shell}-edge`);
  const silk = cssColor(device.silk);
  const desk = cssColor('--pm-bg');
  const muted = cssColor('--pm-text-muted');
  const accent = cssColor('--accent') || cssColor('--lcd-glow');

  // Desk
  ctx.fillStyle = desk;
  ctx.fillRect(0, 0, S, S);

  // Device crop: the top of the shell, running off the bottom of the frame.
  const bx = 70; const by = 90; const bw = S - 140; const bh = S - 160;
  ctx.fillStyle = shell;
  roundRect(ctx, bx, by, bw, bh, 72);
  ctx.fill();
  ctx.strokeStyle = edge;
  ctx.lineWidth = 6;
  roundRect(ctx, bx + 3, by + 3, bw - 6, bh - 6, 70);
  ctx.stroke();

  // Silkscreen: model name
  ctx.fillStyle = silk;
  ctx.font = `800 34px ${cssColor('--font-ui') || 'sans-serif'}`;
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(device.name, bx + 64, by + 78);

  // Bezel + glass
  const gx = bx + 50; const gy = by + 110; const gw = bw - 100; const gh = bh - 210;
  ctx.fillStyle = desk;
  roundRect(ctx, gx - 18, gy - 18, gw + 36, gh + 36, 34);
  ctx.fill();
  ctx.fillStyle = colours.glass;
  roundRect(ctx, gx, gy, gw, gh, 16);
  ctx.fill();

  const pad = 44;
  const innerX = gx + pad;
  const innerW = gw - pad * 2;
  const dotFont = device.font === 'color'
    ? (cssColor('--font-ui') || 'sans-serif')
    : (cssColor('--font-dot') || 'monospace');
  const segFont = cssColor('--font-seg') || dotFont;

  // Status line
  const who = String(friendName(page.peer) || formatNumber(page.peer)).toUpperCase();
  const head = `${page.dir === 'out' ? 'PAGE TO' : 'PAGE FROM'} ${who}`;
  let y = gy + pad + 40;
  if (device.lcd === 'color' && colours.accent) {
    ctx.fillStyle = colours.accent;
    roundRect(ctx, gx, gy, gw, 96, [16, 16, 0, 0]);
    ctx.fill();
    ctx.fillStyle = colours.glass;
  } else {
    ctx.fillStyle = colours.ink;
  }
  ctx.font = `44px ${dotFont}`;
  ctx.fillText(head, innerX, y);
  const time = formatTime(page.sentAt);
  const tw = ctx.measureText(time).width;
  ctx.fillText(time, innerX + innerW - tw, y);

  // Rule under the status line
  ctx.fillStyle = colours.ink;
  ctx.globalAlpha = 0.25;
  ctx.fillRect(innerX, y + 26, innerW, 4);
  ctx.globalAlpha = 1;
  y += 64;

  // Body
  const body = String(page.body ?? '');
  const hint = hintFor(body);
  const bodyTop = y;
  const bodyBottom = gy + gh - pad - (hint ? 70 : 0);
  const seg = device.family === 'numeric' && SEG_ONLY.test(body.trim()) && /\d/.test(body);

  ctx.fillStyle = colours.ink;
  if (seg) {
    const text = body.trim().replace(/ /g, '!');
    let size = 150;
    ctx.font = `${size}px ${segFont}`;
    while (ctx.measureText(text).width > innerW && size > 48) {
      size -= 6;
      ctx.font = `${size}px ${segFont}`;
    }
    const by2 = bodyTop + (bodyBottom - bodyTop) / 2 + size / 2;
    if (colours.ghost) {
      ctx.fillStyle = colours.ghost;
      ctx.fillText('8'.repeat([...text].length), innerX, by2);
    }
    ctx.fillStyle = colours.ink;
    ctx.fillText(text, innerX, by2);
  } else {
    // Largest size whose wrapped lines fit the glass.
    let size = 128;
    let lines = [];
    for (; size >= 36; size -= 6) {
      ctx.font = `${size}px ${dotFont}`;
      lines = layout(ctx, body, size, innerW);
      if (lines.length * size * 1.08 <= bodyBottom - bodyTop) break;
    }
    const lh = size * 1.08;
    const shown = Math.min(lines.length, Math.floor((bodyBottom - bodyTop) / lh) || 1);
    let ly = bodyTop + Math.max(0, (bodyBottom - bodyTop - shown * lh) / 2) + size * 0.9;
    for (const line of lines) {
      if (ly > bodyBottom + size * 0.2) break;
      drawCells(ctx, line, innerX, ly, size, colours.tint);
      ly += lh;
    }
  }

  // Codebook hint
  if (hint) {
    const code = body.trim().split(/\s/)[0].replace(/[^\d-]+$/u, '');
    ctx.font = `46px ${dotFont}`;
    ctx.fillStyle = colours.ink;
    ctx.fillText(`${code} = ${hint.toUpperCase()}`, innerX, gy + gh - pad);
  }

  // Stamp under the glass, on the shell
  ctx.fillStyle = silk;
  ctx.font = `600 30px ${cssColor('--font-ui') || 'sans-serif'}`;
  ctx.fillText(formatStamp(page.sentAt), gx, gy + gh + 64);

  // Footer
  const host = (() => {
    try { return new URL(shareOrigin()).host; } catch { return ''; }
  })();
  ctx.fillStyle = desk;
  ctx.fillRect(0, S - 70, S, 70);
  ctx.font = `600 30px ${cssColor('--font-ui') || 'sans-serif'}`;
  ctx.fillStyle = muted;
  const footer = host ? `PAGE ME · ${host}` : 'PAGE ME';
  const fw = ctx.measureText(footer).width;
  ctx.fillText(footer, (S - fw) / 2, S - 26);
  // One lime dot: the backlight.
  ctx.fillStyle = accent;
  ctx.beginPath();
  ctx.arc((S - fw) / 2 - 26, S - 36, 8, 0, Math.PI * 2);
  ctx.fill();

  return canvas;
}

/**
 * Render and share. Returns 'shared' | 'aborted' | 'downloaded', or throws a
 * sentence-case Error the caller's useAction() shows beside the control.
 */
export async function sharePageImage(page, device) {
  if (!page) throw new Error('There is no page to share.');
  if (typeof document === 'undefined') throw new Error("Sharing images isn't available here.");
  const canvas = await renderPageImage(page, device);
  const blob = await canvasToBlob(canvas, 'image/png');
  if (!blob) throw new Error("Couldn't make the image. Try again.");
  const stamp = String(page.sentAt || Date.now());
  return shareOrDownload(blob, `page-me-${stamp}.png`, 'A page from Page Me');
}
